import {
  FormControl,
  Grid,
  InputLabel,
  TextField,
  Tooltip,
  useColorScheme
} from '@mui/material';
import { useAtom } from 'jotai';
import get from 'lodash/get';
import { Controller, type Path, useFormContext } from 'react-hook-form';

import {
  type Organization,
  organizationsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';

import ErrorMessage from '../ErrorMessage/ErrorMessage';
import SOURCE_TYPE from '../SourceCreate.constants';
import {
  originalSourceTypesAtom,
  selectedSourceDetailsAtom,
  sourceDialogModeAtom
} from '../SourceCreate.store';
import useStyles from '../SourceCreate.styles';
import {
  type SourceCreateDynamicFormModel,
  SourceDialogMode
} from '../SourceCreate.types';
import type { SiteNameFieldProps } from './SiteNameField.types';

// Reusable site name component for root and Microsoft data providers.
const SiteNameField = ({
  name,
  srcTypeId,
  dataSourceName,
  xs,
  sm,
  md
}: SiteNameFieldProps) => {
  const { register, control, formState } =
    useFormContext<SourceCreateDynamicFormModel>();

  const { errors } = formState;
  const { mode: themeMode } = useColorScheme();
  const { classes } = useStyles({ themeMode });
  const [mode] = useAtom(sourceDialogModeAtom);
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [organizations] = useAtom(organizationsAtom);
  const [originalSourceTypes] = useAtom(originalSourceTypesAtom);
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);

  // Get the source name prefix based on the source type and data source name
  const getSourceNamePrefix = (
    sourceTypeId: number,
    providerName: string
  ): string => {
    const orgLabel =
      mode === SourceDialogMode.New
        ? (selectedOrgData?.label ?? '')
        : (organizations.find(
            (org: Organization) =>
              org.id === selectedSourceDetails?.organization_id
          )?.label ?? '');

    const srcTypeName =
      originalSourceTypes?.find((item) => item.id === sourceTypeId)?.name || '';
    const typeName = srcTypeName?.replace(/\s+/g, '').trim() ?? '';

    // Determine API endpoint name for Microsoft Entra ID (id = 36)
    let apiEndpointName = '';
    if (sourceTypeId === SOURCE_TYPE.MICROSOFT_ENTRA_ID) {
      apiEndpointName = providerName === 'GraphAPI' ? 'Graph' : 'O365';
    }

    // Build parts dynamically and filter out empty ones
    const parts = [orgLabel, typeName, apiEndpointName].filter(Boolean);

    return parts.join('-');
  };
  const prefix = getSourceNamePrefix(srcTypeId, dataSourceName);
  const prefixFieldName =
    `${name}_prefix` as Path<SourceCreateDynamicFormModel>;
  const fieldError = get(errors, name);

  return (
    <Grid size={{ xs, sm, md }}>
      <InputLabel>Site</InputLabel>

      <FormControl fullWidth sx={{ display: 'flex', flexDirection: 'row' }}>
        {/* Prefix (read-only) */}
        <Tooltip title={prefix} placement="top-start">
          <TextField
            {...register(prefixFieldName, {})}
            disabled
            value={prefix}
            className={classes.inputDisabled}
          />
        </Tooltip>

        {/* Editable site name */}
        <Controller
          name={name as Path<SourceCreateDynamicFormModel>}
          control={control}
          defaultValue=""
          rules={{
            validate: (value) =>
              /^[a-zA-Z0-9-_]*$/.test(String(value ?? '')) ||
              'Allowed: letters, numbers, hyphen (-), underscore (_)'
          }}
          render={({ field }) => (
            <TextField
              className={classes.inputEditable}
              error={!!fieldError}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
        {fieldError && <ErrorMessage message={get(fieldError, 'message')} />}
      </FormControl>
    </Grid>
  );
};

export default SiteNameField;
